import React from 'react';
import styled from 'styled-components';
import { Wrapper } from '@catalogo/theme-wrapper';
import ProductMainArea from '@catalogo/ui-americanas-mobile-product-main-area';
import BlackboxPosition from '@catalogo/core-blackbox/position';
import { hasCriticalErrors, has404OnPath } from '@catalogo/core-graphql-errors';
import { FullPageLoading } from '@catalogo/ui-americanas-mobile-loading';
import { getIn } from 'seamless-immutable';
import Error from '@catalogo/template-americanas-mobile-error';
import { getCurrentSku } from '@catalogo/core-sku';
import QnA from '@catalogo/ui-americanas-mobile-qna/src/components/template/TemplateQnA/index';
import Suggestions from '@catalogo/ui-americanas-mobile-suggestions';
import MundoDrawer from '@catalogo/ui-americanas-mobile-mundo-drawer';
import Reviews from '@catalogo/ui-americanas-mobile-product-review';
import Carousel from '@catalogo/ui-americanas-mobile-carousel';
import { withMetrics } from '@catalogo/core-metrics';
import { useProductPage } from '@catalogo/service-americanas-product/src/hooks/product-page';
import { fmtProduct } from '@catalogo/service-americanas-product/src/helpers/product-page';
import { Redirect } from '@catalogo/core-redirect';
import Schema from './schema';
import ReportLink from './components/report-link';
import BottomAds from './components/bottom-ads';

// eslint-disable-next-line max-statements
export const ProductTemplate = props => {
  const { match, routeId, parsedQs, history, dispatchMetrics } = props;
  const sellerId = getIn(parsedQs, ['sellerId'], '');
  const offerId = getIn(parsedQs, ['offerId'], '');
  const productId = getIn(match, ['params', 'id']);
  const path = `/produto/${productId}`;

  const { data, error, called, loading, refetch, reviewsQueries } = useProductPage({
    path,
    sellerId,
    offerId,
    productId,
    routeId,
    dispatchMetrics,
  });
  const productName = data?.product?.name;

  if (loading || !called) {
    return <FullPageLoading />;
  }

  if (getIn(error, ['graphQLErrors']) && has404OnPath(error.graphQLErrors, ['product'])) {
    return <Redirect status={302} url="/" />;
  }

  if (getIn(error, ['graphQLErrors']) && hasCriticalErrors(error.graphQLErrors, ['product'])) {
    return <Error retry={() => refetch()} />;
  }

  if (data) {
    const currentSku = getCurrentSku(data, parsedQs);
    const components = getIn(data, ['page', 'components'], {});
    const isInternational = getIn(currentSku, ['offers', 'result', '0', 'currency']) === 'USD';
    const breadcrumb = getIn(data, ['page', 'breadcrumb'], []);
    const product = fmtProduct(getIn(data, ['product']), reviewsQueries);

    return (
      <Wrapper className="Product" color="#f1f1f1">
        <Schema currentSku={currentSku} {...props} {...data} reviewsQueries={reviewsQueries} />
        <Container>
          <BlackboxPosition position="produto-top" publication={getIn(components, ['produto-top'])} />
        </Container>
        <ProductMainArea
          currentSku={currentSku}
          {...data}
          {...props}
          id={productId}
          recData={components}
          product={product}
        />
        <ReportLink id={productId} />
        <Carousel position="item_page.rr1" publication={getIn(components, ['item_page.rr1'])} />
        <Carousel position="item_page.ads" publication={getIn(components, ['item_page.ads'])} />
        <Carousel position="item_page.rr2" publication={getIn(components, ['item_page.rr2'])} />
        {isInternational && <MundoDrawer />}
        <div>
          <Reviews
            productId={productId}
            history={history}
            reviewsQueries={reviewsQueries}
            breadcrumb={breadcrumb}
            productName={productName}
          />
        </div>
        <QnA product={product} breadcrumb={breadcrumb} components={components} currentSku={currentSku} />
        <BlackboxPosition position="item_page.rr3" publication={getIn(components, ['item_page.rr3'])} />
        <BlackboxPosition position="item_page.history" publication={getIn(components, ['item_page.history'])} />
        <BottomAds position="produto-bottom" publication={getIn(components, ['produto-bottom'])} />
        <BlackboxPosition position="schema" publication={getIn(components, ['schema'])} />
        <Suggestions {...props} {...data} />
      </Wrapper>
    );
  }

  return null;
};

const Container = styled.div`
  .spacey-image,
  img,
  picture {
    max-height: 44px;
  }
`;

export default withMetrics(ProductTemplate);
